import React, { useState } from "react";
import { classifyRisk } from "../../actions/actions";

const DesktopResults = ({ filteredColleges, rankKey, formData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: rankKey,
    direction: "asc",
  });
  const itemsPerPage = 10;

  // Sorting function
  const sortData = (data, sortKey, direction) => {
    return [...data].sort((a, b) => {
      let valueA = a[sortKey];
      let valueB = b[sortKey];

      // --- Start of Change: Handle non-numeric ranks ---
      if (sortKey === rankKey) {
        const isARankless = !/^\d+$/.test(valueA);
        const isBRankless = !/^\d+$/.test(valueB);

        if (isARankless && isBRankless) return 0;
        if (isARankless) return 1;
        if (isBRankless) return -1;
      }
      // --- End of Change ---

      // Handle numeric values for closing rank and fee
      if (sortKey === rankKey || sortKey === "college_fee") {
        valueA = parseFloat(valueA) || 0;
        valueB = parseFloat(valueB) || 0;
      } else {
        // Handle string values
        valueA = valueA ? valueA.toString().toLowerCase() : "";
        valueB = valueB ? valueB.toString().toLowerCase() : "";
      }

      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Handle header click for sorting
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Get sorted data
  const sortedColleges = sortConfig.key
    ? sortData(filteredColleges, sortConfig.key, sortConfig.direction)
    : filteredColleges;

  // Pagination calculations
  const totalPages = Math.ceil(sortedColleges.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentColleges = sortedColleges.slice(startIndex, endIndex);

  // Pagination navigation
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];

    // Always show first page
    buttons.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
          currentPage === 1
            ? "bg-blue-600 text-white"
            : "text-gray-700 bg-white hover:bg-gray-50"
        }`}
      >
        1
      </button>
    );

    // Show ellipsis if there’s a gap after the first page
    if (currentPage > 3 && totalPages > 3) {
      buttons.push(
        <span
          key="ellipsis-start"
          className="px-3 py-1 text-sm font-medium text-gray-700"
        >
          ...
        </span>
      );
    }

    // Define the range of pages around the current page
    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);

    // Add pages in the range
    for (let page = startPage; page <= endPage; page++) {
      buttons.push(
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
            currentPage === page
              ? "bg-blue-600 text-white"
              : "text-gray-700 bg-white hover:bg-gray-50"
          }`}
        >
          {page}
        </button>
      );
    }

    // Show ellipsis if there’s a gap before the last page
    if (currentPage < totalPages - 2 && totalPages > 3) {
      buttons.push(
        <span
          key="ellipsis-end"
          className="px-3 py-1 text-sm font-medium text-gray-700"
        >
          ...
        </span>
      );
    }

    // Show last page if there’s more than one page
    if (totalPages > 1) {
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
            currentPage === totalPages
              ? "bg-blue-600 text-white"
              : "text-gray-700 bg-white hover:bg-gray-50"
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="hidden md:block overflow-x-auto">
      <div className="border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: "inst_code", label: "Code" },
                { key: "inst_name", label: "College Name" },
                { key: "district", label: "District" },
                { key: "place", label: "Location" },
                { key: "branch_code", label: "Branch" },
                { key: rankKey, label: "Closing Rank" },
                { key: "college_fee", label: "Fee" },
                { key: "type", label: "Type", sortable: false },
              ].map((header) => (
                <th
                  key={header.key}
                  className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    header.sortable !== false
                      ? "cursor-pointer hover:bg-gray-100"
                      : ""
                  } ${
                    header.key === "contact"
                      ? "hidden xl:table-cell"
                      : header.key === "college_fee"
                      ? "hidden lg:table-cell"
                      : header.key === "place"
                      ? "hidden md:table-cell"
                      : ""
                  }`}
                  onClick={
                    header.sortable !== false
                      ? () => handleSort(header.key)
                      : undefined
                  }
                >
                  <div className="flex items-center">
                    {header.label}
                    {header.sortable !== false &&
                      sortConfig.key === header.key && (
                        <span className="ml-1">
                          {sortConfig.direction === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {currentColleges.map((college, index) => {
              const collegeRank = parseInt(college[rankKey]) || 0;
              const studentRank = parseInt(formData.rank) || 0;
              const riskClass = classifyRisk(studentRank, collegeRank);

              const riskBgClass =
                riskClass === "safety-safe"
                  ? "bg-green-50"
                  : riskClass === "safety-moderate"
                  ? "bg-yellow-50"
                  : "bg-red-50";

              return (
                <tr key={index} className={riskBgClass}>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {college.inst_code}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900 max-w-xs truncate">
                    <div className="flex space-x-2">
                      {college.inst_name}
                      {college.website && (
                        <a
                          href={
                            college.website.startsWith("http")
                              ? college.website
                              : `http://${college.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-blue-600 hover:text-blue-800"
                          title="Visit college website"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {college.dist}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                    {college.place ? (
                      <a
                        href={
                          college.location_links ||
                          `https://www.google.com/maps/search/${encodeURIComponent(
                            college.inst_name + " " + college.place
                          )}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        {college.place}
                      </a>
                    ) : (
                      <span className="text-gray-400">No location data</span>
                    )}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {college.branch_code}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {college[rankKey]}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 hidden lg:table-cell">
                    {college.college_fee
                      ? `₹${college.college_fee.toLocaleString()}`
                      : "Not available"}
                  </td>
               

                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                
                    {college.type}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to{" "}
            {Math.min(endIndex, sortedColleges.length)} of{" "}
            {sortedColleges.length} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            {renderPaginationButtons()}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopResults;
